import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "../../../../components/navbar/navbar";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { sentActionLog } from "../../../../utils/common";
import CarruselReorder from "./CarruselReorder";
import CarruselPreview from "./CarruselPreview";
import {
  construirItemsDeCarrusel,
  opcionesDeCarrusel,
  useReordenBanners,
} from "./useReordenBanners";
import { ReordenItem } from "./reorden.types";

const getToken = () => localStorage.getItem("jwt");

/**
 * Vista /admin-cms-reorden — Reordenamiento del banner en carrusel.
 *
 * Dos zonas:
 *  - Zona A: reordenamiento drag-and-drop de los banners del carrusel seleccionado.
 *  - Zona B: preview en vivo del carrusel con el orden actual.
 *
 * El nuevo orden se persiste con "Guardar orden" (PATCH /cms/banners/reordenar).
 */
const ReordenView: React.FC = () => {
  const navigate = useNavigate();
  const { banners, loading, error, saving, fetchBanners, guardarOrden } =
    useReordenBanners();

  // Filtros del carrusel.
  const [tipoAplicacion, setTipoAplicacion] = useState("");
  const [tipo, setTipo] = useState("");
  const [seccion, setSeccion] = useState("");
  const [formato, setFormato] = useState("");

  // Estado local del orden (se edita al arrastrar).
  const [items, setItems] = useState<ReordenItem[]>([]);
  // Copia del orden ya persistido, para revertir si el guardado falla.
  const [itemsGuardados, setItemsGuardados] = useState<ReordenItem[]>([]);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  // Hay cambios sin guardar si el orden actual difiere del ultimo guardado.
  const hayCambios = useMemo(
    () =>
      items.map((i) => i.idDistribucion).join(",") !==
      itemsGuardados.map((i) => i.idDistribucion).join(","),
    [items, itemsGuardados]
  );

  // Auth guard: si no hay token, redirige. La validez real del token la impone el
  // backend en cada llamada (JWT). No bloqueamos la navegacion cuando el middleware
  // de verificacion no responde (p.ej. entorno local sin Django): en ese caso el
  // backend rechazara con 401 si el token no sirve, en vez de expulsar al usuario.
  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate("/");
    }
  }, [navigate]);

  // Carga inicial de banners + log de acceso.
  useEffect(() => {
    fetchBanners();
    sentActionLog("ACCESO", "Vista reordenamiento de carrusel (solo visual)");
  }, [fetchBanners]);

  // Opciones de filtro derivadas de los banners reales (carruseles en la BD).
  const opciones = useMemo(
    () =>
      opcionesDeCarrusel(banners, { tipoAplicacion, tipo, seccion, formato }),
    [banners, tipoAplicacion, tipo, seccion, formato]
  );

  const seleccionCompleta = Boolean(
    tipoAplicacion && tipo && seccion && formato
  );

  // Recalcula los items del carrusel cuando cambia la seleccion o los banners.
  useEffect(() => {
    if (!seleccionCompleta) {
      setItems([]);
      setItemsGuardados([]);
      return;
    }
    const nuevos = construirItemsDeCarrusel(banners, {
      tipoAplicacion,
      tipo,
      seccion,
      formato,
    });
    setItems(nuevos);
    setItemsGuardados(nuevos); // el orden recien cargado es el "persistido"
  }, [banners, tipoAplicacion, tipo, seccion, formato, seleccionCompleta]);

  // Guarda el nuevo orden (optimistic): si falla, revierte al ultimo guardado.
  const handleGuardar = async () => {
    const previo = itemsGuardados;
    const nuevo = items;
    setItemsGuardados(nuevo); // optimista
    const ok = await guardarOrden(nuevo);
    if (ok) {
      setOkMsg("Orden guardado correctamente");
      sentActionLog(
        "REORDEN",
        `Guardado orden carrusel ${tipoAplicacion}/${tipo}/${seccion}/${formato}`
      );
    } else {
      // Revertir: el estado 'guardado' vuelve al previo y el orden visible tambien.
      setItemsGuardados(previo);
      setItems(previo);
    }
  };

  return (
    <>
      <NavBar />
      <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography variant="h5">Reordenamiento de carrusel</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Selecciona un carrusel, arrastra los banners para reordenarlos, revisa la
        vista previa y guarda el nuevo orden.
      </Typography>

      {/* Selección del carrusel */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            "& > *": { flex: "1 1 200px", minWidth: 180 },
          }}
        >
          <FormControl fullWidth size="small">
            <InputLabel>Aplicación</InputLabel>
            <Select
              label="Aplicación"
              value={tipoAplicacion}
              onChange={(e) => {
                setTipoAplicacion(e.target.value);
                setTipo("");
                setSeccion("");
                setFormato("");
              }}
            >
              {opciones.tipoAplicacion.map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" disabled={!tipoAplicacion}>
            <InputLabel>Tipo</InputLabel>
            <Select
              label="Tipo"
              value={tipo}
              onChange={(e) => {
                setTipo(e.target.value);
                setSeccion("");
                setFormato("");
              }}
            >
              {opciones.tipo.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" disabled={!tipo}>
            <InputLabel>Sección</InputLabel>
            <Select
              label="Sección"
              value={seccion}
              onChange={(e) => {
                setSeccion(e.target.value);
                setFormato("");
              }}
            >
              {opciones.seccion.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" disabled={!seccion}>
            <InputLabel>Formato</InputLabel>
            <Select
              label="Formato"
              value={formato}
              onChange={(e) => setFormato(e.target.value)}
            >
              {opciones.formato.map((f) => (
                <MenuItem key={f} value={f}>
                  {f}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Solo se listan carruseles existentes (tipoVista = CARRUSEL) según los banners dados de alta.
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : !seleccionCompleta ? (
        <Alert severity="info">
          Elige aplicación, tipo, sección y formato para ver los banners del
          carrusel.
        </Alert>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            "& > *": { flex: "1 1 320px", minWidth: 280 },
          }}
        >
          {/* Zona A — Reordenamiento */}
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="subtitle1">Reordenar</Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                disabled={!hayCambios || saving || items.length === 0}
                onClick={handleGuardar}
              >
                {saving ? "Guardando..." : "Guardar orden"}
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <CarruselReorder items={items} onChange={setItems} />
          </Box>

          {/* Zona B — Preview del carrusel */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Vista previa del carrusel
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <CarruselPreview items={items} />
          </Box>
        </Box>
      )}

      <Snackbar
        open={Boolean(okMsg)}
        autoHideDuration={3000}
        onClose={() => setOkMsg(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setOkMsg(null)}>
          {okMsg}
        </Alert>
      </Snackbar>
    </Container>
    </>
  );
};

export default ReordenView;
